import { useEffect, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Button from 'components/button/Button';
import Modal from 'components/modal';
import {
  HeaderContainer,
  ModalActions,
  ModalContainer,
  ModalContent,
} from 'components/modal/styles';
import Notification from 'components/notification';
import Radio from 'components/radio/Radio';

import { DeadlineApplyMode, DeadlinePolicy } from 'contexts/playlists/types';
import { deleteDeadlinePolicy, upsertDeadlinePolicy } from 'services/playlists';
import {
  DEFAULT_DEADLINE_APPLY_MODE_OPTIONS,
  DEFAULT_DEADLINE_COPY,
  DEFAULT_DEADLINE_ERROR_MESSAGE,
  DEFAULT_DEADLINE_SUCCESS_MESSAGE,
  TEAM_DEFAULT_DEADLINE_APPLY_MODE_OPTIONS,
  TEAM_DEFAULT_DEADLINE_COPY,
  TEAM_DEFAULT_DEADLINE_ERROR_MESSAGE,
  TEAM_DEFAULT_DEADLINE_SUCCESS_MESSAGE,
} from 'constants/playlists';

import DeadlineSelector, {
  DEFAULT_TRAINING_PACE,
  DeadlineSelectorValue,
} from '../ManageDeadlineDrawer/DeadlineSelector';
import {
  DrawerBody,
  FooterActions,
  PlaylistDurationText,
  SectionTitle,
} from '../ManageDeadlineDrawer/styles';
import {
  ApplyModeOptionDescription,
  ApplyModeOptionLabel,
  ApplyModeSection,
  DefaultDeadlineModalGlobalStyles,
  Disclaimer,
} from './styles';

interface DefaultDeadlineDrawerBaseProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  playlistId: string;
  businessAccountId: string;
  playlistDurationSeconds?: number;
  currentPolicy: DeadlinePolicy | null;
  hasAssignedUsers: boolean;
}

export type DefaultDeadlineDrawerProps = DefaultDeadlineDrawerBaseProps &
  ({ scope?: 'account'; teamId?: never } | { scope: 'team'; teamId: string });

const getInitialValue = (currentPolicy: DeadlinePolicy | null): DeadlineSelectorValue => {
  if (currentPolicy?.type === 'recommended') {
    return {
      type: 'recommended',
      date: null,
      trainingPaceHoursPerWeek: currentPolicy.training_pace,
    };
  }

  return { type: 'none', date: null };
};

const formatPlaylistDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return `${h}h ${m}m`;
};

const DefaultDeadlineDrawer = ({
  visible,
  onClose,
  onSuccess,
  playlistId,
  playlistDurationSeconds,
  currentPolicy,
  hasAssignedUsers,
  scope = 'account',
  teamId,
}: DefaultDeadlineDrawerProps): JSX.Element | null => {
  const { playlistDeadlinesPhase2 } = useFlags();
  const copy = scope === 'team' ? TEAM_DEFAULT_DEADLINE_COPY : DEFAULT_DEADLINE_COPY;
  const successMessage =
    scope === 'team' ? TEAM_DEFAULT_DEADLINE_SUCCESS_MESSAGE : DEFAULT_DEADLINE_SUCCESS_MESSAGE;
  const errorMessage =
    scope === 'team' ? TEAM_DEFAULT_DEADLINE_ERROR_MESSAGE : DEFAULT_DEADLINE_ERROR_MESSAGE;
  const applyModeOptions =
    scope === 'team'
      ? TEAM_DEFAULT_DEADLINE_APPLY_MODE_OPTIONS
      : DEFAULT_DEADLINE_APPLY_MODE_OPTIONS;
  const team_ids = scope === 'team' && teamId ? [teamId] : undefined;

  const initialValue = useMemo(() => getInitialValue(currentPolicy), [currentPolicy]);
  const initialApplyMode: DeadlineApplyMode = currentPolicy?.apply_to ?? 'future';

  const [value, setValue] = useState<DeadlineSelectorValue>(initialValue);
  const [applyMode, setApplyMode] = useState<DeadlineApplyMode>(initialApplyMode);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    setValue(initialValue);
    setApplyMode(initialApplyMode);
  }, [initialValue, initialApplyMode, visible]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!playlistDeadlinesPhase2) return null;

  const pace = value.trainingPaceHoursPerWeek ?? DEFAULT_TRAINING_PACE;

  // DEV-6548: offered for both No deadline and Recommended, for both scopes, whenever
  // there are assigned users to apply the change to.
  const showApplyModeSection = hasAssignedUsers;

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);

    try {
      if (value.type === 'recommended') {
        const applyTo = showApplyModeSection ? applyMode : 'future';
        await upsertDeadlinePolicy({
          playlistId,
          training_pace: pace,
          apply_to: applyTo,
          scope,
          team_ids,
        });
      } else {
        // Always sent, with no existing policy or not (DEV-6630/business-api#1165): "all" clears
        // every current deadline, custom included, and "future" is a safe no-op either way.
        const applyTo = showApplyModeSection ? applyMode : 'future';
        await deleteDeadlinePolicy(playlistId, { scope, team_ids, apply_to: applyTo });
      }

      onClose();
      onSuccess();
      Notification({ text: successMessage, type: 'success', singleLine: true });
    } catch (error) {
      Notification({ text: errorMessage, type: 'error' });
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <DefaultDeadlineModalGlobalStyles />
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={595}
        closeIconColor="#fff"
        wrapClassName="default-deadline-modal"
        destroyOnClose
      >
        <ModalContainer>
          <HeaderContainer>
            <h2>{copy.drawerTitle}</h2>
          </HeaderContainer>

          <ModalContent>
            <DrawerBody>
              <div>
                <SectionTitle>{copy.sectionTitle}</SectionTitle>
                <Disclaimer>{copy.disclaimer}</Disclaimer>
                {playlistDurationSeconds != null && playlistDurationSeconds > 0 && (
                  <PlaylistDurationText>
                    Playlist duration:{' '}
                    <span>{formatPlaylistDuration(playlistDurationSeconds)}</span>
                  </PlaylistDurationText>
                )}
              </div>

              <DeadlineSelector
                value={value}
                onChange={setValue}
                playlistDurationSeconds={playlistDurationSeconds}
                hideCustom
                recommendedPreviewMode="weeks"
              />

              {showApplyModeSection && (
                <ApplyModeSection role="radiogroup" aria-label={copy.applyToLabel}>
                  <SectionTitle>{copy.applyToLabel}</SectionTitle>
                  {applyModeOptions.map((option) => (
                    <Radio
                      key={option.value}
                      aria-label={option.label}
                      label={
                        <>
                          <ApplyModeOptionLabel>{option.label}</ApplyModeOptionLabel>
                          <ApplyModeOptionDescription>
                            {option.description}
                          </ApplyModeOptionDescription>
                        </>
                      }
                      checked={applyMode === option.value}
                      controlled
                      onChange={() => setApplyMode(option.value)}
                    />
                  ))}
                </ApplyModeSection>
              )}
            </DrawerBody>
          </ModalContent>

          <ModalActions>
            <FooterActions>
              <Button
                aria-label="Save deadline"
                $primary
                loading={saving}
                disabled={saving}
                onClick={handleSave}
              >
                {copy.saveButtonLabel}
              </Button>
            </FooterActions>
          </ModalActions>
        </ModalContainer>
      </Modal>
    </>
  );
};

export default DefaultDeadlineDrawer;
